import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'

import media from 'media'
import theme from 'theme'

import IconChevronRight from '@anvilco-pkg/ui/components/Icon/IconChevronRight'
import Markdown from '@anvilco-pkg/ui/components/Markdown'

import { calculateLength, isBrowser } from 'helpers'
import { DEFAULT_BODY_FONT_SIZE_STRING } from 'consts'

import getContentElement from 'lib/content/sanity'
import withJsonLd from 'lib/json-ld/withJsonLd'

const CLASSNAME_HOVER = 'faq-hover'

const Container = styled(Box).attrs({})``

const QuestionContainer = styled(Flex).attrs({
  justifyContent: 'space-between',
  alignItems: 'center',
})``

const Question = styled.div`
  font-weight: ${theme.fontWeights.bold};
  font-size: ${({ theme }) => theme?.fontSizes?.mobile?.body};

  ${media.desktop`
      font-size: ${({ theme }) =>
        calculateLength(
          theme?.fontSizes?.body || DEFAULT_BODY_FONT_SIZE_STRING,
          '+',
          2
        )};
  `}
`

const Answer = styled.div`
  font-size: ${({ theme }) => theme?.fontSizes?.mobile?.body};
  ${media.desktop`
      font-size: ${({ theme }) =>
        theme?.fontSizes?.body || DEFAULT_BODY_FONT_SIZE_STRING};
  `}

  opacity: 0;
  transition:
    opacity 0.9s ease,
    height 0.5s ease,
    margin-top 0.5s ease;
  height: 0;
`

const StyledIconChevronRight = styled(IconChevronRight)`
  transform: rotate(0deg);
  transition: transform 0.5s ease;
  margin-left: 16px;
  min-width: 16px;
  min-height: 16px;
`

export const Item = styled(Box).attrs({
  width: [1],
  py: 20,
})`
  border-bottom: 1px solid ${theme.colors.blacks[20]};
  position: relative;

  &.${CLASSNAME_HOVER} {
    ${Answer} {
      opacity: 1;
      margin-top: 12px;
      height: initial;
    }

    ${StyledIconChevronRight} {
      transform: rotate(90deg);
    }
  }
`

function FAQs ({ items, ...others }) {
  let handleMouseMove

  useEffect(() => {
    if (isBrowser()) {
      const faqItems = document.querySelectorAll(`${Item}`)

      let mouseStopTimeout = null
      handleMouseMove = (e) => {
        const handleFAQToggle = () => {
          // find the faqItem to open
          const faqItem = Array.from(faqItems)?.find((element) =>
            element.contains(e.target)
          )
          if (faqItem) {
            // close any other FAQ items
            Array.from(faqItems).forEach((element) =>
              element.classList?.remove(CLASSNAME_HOVER)
            )

            faqItem?.classList?.add(CLASSNAME_HOVER)
          }
        }

        if (mouseStopTimeout) clearTimeout(mouseStopTimeout)
        mouseStopTimeout = setTimeout(handleFAQToggle, 80)
      }

      document.addEventListener('mousemove', handleMouseMove)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  return (
    <Container {...others}>
      {items.map(({ question, answer, answerIsNotMarkdown, sanityAnswer }) => (
        <Item key={question}>
          <QuestionContainer>
            <Question>{question}</Question>
            <StyledIconChevronRight fill={theme.colors.greens[60]} />
          </QuestionContainer>
          {answer && (
            <Answer as={!answerIsNotMarkdown ? Markdown : 'div'}>
              {answer}
            </Answer>
          )}
          {!answer && sanityAnswer && (
            <Answer>{getContentElement({ content: sanityAnswer })}</Answer>
          )}
        </Item>
      ))}
    </Container>
  )
}

export default withJsonLd(FAQs, {
  jsonLdSchemaName: 'FAQPage',
  getCreateArgs: ({ items }) => ({ faqs: items }),
})
